<template>
    <!-- Un texto por idioma de la ficha (reglas, sobre nosotros, espacios
         comunes, cocina, aseo). Para todos se ve igual que antes; el admin
         además tiene "Editar", que lo edita ahí mismo. -->
    <div class="hf-txt">
        <template v-if="!editando">
            <div v-if="html" class="da-prose" v-html="html" />
            <p v-else class="da-empty-state">{{ vacio }}</p>
            <button v-if="isAdmin" type="button" class="hf-txt__edit" @click="abrir">
                <i class="fas fa-pen" aria-hidden="true" /> {{ $t('admin_tools.edit.edit') }}
            </button>
        </template>

        <form v-else class="hf-txt__form" @submit.prevent="guardar">
            <div class="hf-txt__langrow">
                <span :id="`hf-txt-lang-${campo}`" class="hf-txt__langlabel">{{ $t('admin_tools.edit.lang_label') }}</span>
                <div class="hf-txt__langs" role="radiogroup" :aria-labelledby="`hf-txt-lang-${campo}`">
                    <button
                        v-for="l in IDIOMAS"
                        :key="l"
                        type="button"
                        role="radio"
                        :aria-checked="idioma === l"
                        :disabled="guardando"
                        :class="['hf-txt__lang', { 'hf-txt__lang--on': idioma === l }]"
                        @click="idioma = l"
                    >{{ l.toUpperCase() }}</button>
                </div>
            </div>
            <textarea
                ref="area"
                v-model="textos[idioma]"
                rows="6"
                maxlength="5000"
                :disabled="guardando"
                :placeholder="$t('admin_tools.edit.empty_lang')"
                :aria-label="`${$t('admin_tools.edit.lang_label')} ${idioma.toUpperCase()}`"
            />
            <small class="hf-txt__hint">{{ $t('admin_tools.edit.hint') }} {{ $t('admin_tools.edit.translate_hint') }}</small>
            <div class="hf-txt__actions">
                <button type="button" class="hf-txt__btn" :disabled="guardando" @click="editando = false">{{ $t('admin_tools.edit.cancel') }}</button>
                <button type="submit" class="hf-txt__btn hf-txt__btn--primary" :disabled="guardando">
                    <b-spinner v-if="guardando" small />
                    {{ $t(guardando ? 'admin_tools.edit.translating' : 'admin_tools.edit.save') }}
                </button>
            </div>
        </form>

        <AdminAlcanceModal v-if="isAdmin" ref="alcance" />
    </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { IDIOMAS, textoHtml, textoPorIdioma, htmlATexto } from '@/Utils/textoIdiomas';
import AdminAlcanceModal from './AdminAlcanceModal';
import guardarFicha from './guardarFicha';

export default {
    name: 'AdminTextoEditable',
    components: { AdminAlcanceModal },
    props: {
        accommodation: { type: Object, required: true },
        campo: { type: String, required: true },   // columna: reglas, sobre_nosotros, …
        vacio: { type: String, default: 'Sin información disponible.' },
    },
    data() {
        return { IDIOMAS, editando: false, guardando: false, idioma: 'es', textos: {} };
    },
    computed: {
        ...mapGetters(['isAdmin']),
        html() {
            return textoHtml(this.accommodation[this.campo], this.$i18n.locale);
        },
    },
    watch: {
        // Otra ficha: se cierra lo que estuviera abierto.
        'accommodation.idalojamiento'() { this.editando = false; },
    },
    methods: {
        abrir() {
            const data = textoPorIdioma(this.accommodation[this.campo]);
            const t = {};
            IDIOMAS.forEach(l => { t[l] = htmlATexto(data[l]); });
            this.textos = t;
            this.idioma = IDIOMAS.indexOf(this.$i18n.locale) !== -1 ? this.$i18n.locale : 'es';
            this.editando = true;
            this.$nextTick(() => this.$refs.area && this.$refs.area.focus());
        },
        // Se guarda lo escrito en el idioma elegido y la API lo traduce a los
        // otros 5 (pisa esos idiomas). Al cambiar de idioma en el selector se
        // muestra lo que ya había en ese idioma, para partir de ahí.
        async guardar() {
            this.guardando = true;
            const ok = await guardarFicha(this, {
                traducir: true,
                origen: this.idioma,
                textos: { [this.campo]: { [this.idioma]: this.textos[this.idioma] || '' } },
            });
            this.guardando = false;
            if (ok) this.editando = false;
        },
    },
};
</script>

<style scoped lang="scss">
.hf-txt {
    &__edit {
        display: inline-flex;
        align-items: center;
        gap: 6px;
        margin-top: 8px;
        padding: 5px 12px;
        border: 1.5px dashed $hf-sec-pine;
        border-radius: 8px;
        background: #fff;
        font-size: .82rem;
        font-weight: 700;
        color: $hf-sec-pine;
        cursor: pointer;
        &:hover { background: #F4F8F6; }
        &:focus-visible { outline: 2px solid #2563EB; outline-offset: 2px; }
    }
    &__form textarea {
        width: 100%;
        min-height: 130px;
        padding: 10px 12px;
        border: 1.5px solid $grey-10;
        border-radius: 8px;
        font-size: .95rem;
        resize: vertical;
        &:focus { outline: none; border-color: $hf-sec-pine; }
    }
    &__langrow { display: flex; flex-wrap: wrap; align-items: center; gap: 8px 12px; margin-bottom: 8px; }
    &__langlabel { font-size: .85rem; font-weight: 700; color: #374151; }
    &__langs { display: flex; flex-wrap: wrap; gap: 4px; }
    &__lang {
        padding: 4px 10px;
        border: 1.5px solid $grey-10;
        border-radius: 6px;
        background: #fff;
        font-size: .78rem;
        font-weight: 700;
        color: #374151;
        cursor: pointer;
        &:disabled { cursor: not-allowed; opacity: .6; }
        &:focus-visible { outline: 2px solid #2563EB; outline-offset: 2px; }
        &--on { background: $hf-sec-pine; border-color: $hf-sec-pine; color: #fff; }
    }
    &__hint { display: block; margin-top: 4px; color: #6B7280; }
    &__actions { display: flex; justify-content: flex-end; gap: 8px; margin-top: 10px; }
    &__btn {
        display: inline-flex;
        align-items: center;
        gap: 6px;
        padding: 7px 14px;
        border: 1.5px solid $grey-10;
        border-radius: 8px;
        background: #fff;
        font-weight: 700;
        color: #374151;
        cursor: pointer;
        &:disabled { opacity: .55; cursor: not-allowed; }
        &--primary { background: $hf-sec-pine; border-color: $hf-sec-pine; color: #fff; }
    }
}
</style>
